import { useCallback, useEffect } from 'react'
import { type FieldValues, type Path, type SubmitErrorHandler, useForm, type UseFormProps, type UseFormReturn } from 'react-hook-form'
import { ValidationError } from '../../utils/errors/ValidationError'
import { addNotification } from 'store/componentsSlice'
import { useDispatch } from 'react-redux'
import useTranslation from './useTranslation'

export type UseCustomFormReturn<Fields extends FieldValues> = Omit<UseFormReturn<Fields>, 'handleSubmit'> & {
  handleSubmit: HandleSubmit<Fields>
  onSelect: (name: keyof FieldValues, code?: string | null, label?: string | null, shouldValidate?: boolean, shouldDirty?: boolean) => void
}

export type HandleSubmit<TFieldValues extends FieldValues> = (
  onValid: (values: TFieldValues) => Promise<unknown> | void,
  onSuccess?: () => void,
  onInvalid?: SubmitErrorHandler<TFieldValues>,
) => (e?: React.BaseSyntheticEvent) => Promise<void>

const useCustomForm = <TFieldValues extends FieldValues = FieldValues, TContext = any>(
  props?: UseFormProps<TFieldValues, TContext> & {
    preserveData?: boolean
    preservationKey?: string
  },
): UseCustomFormReturn<TFieldValues> => {
  const useFormResult = useForm<TFieldValues>(props)
  const dispatch = useDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    if (props?.preserveData) {
      // useFormResult.watch((values) => {
      //   if (preservationTimeout.current) {
      //     clearTimeout(preservationTimeout.current);
      //   }
      //   preservationTimeout.current = setTimeout(() => {
      //     sessionStorage.setItem(
      //       preservationKey.current,
      //       JSON.stringify(values)
      //     );
      //   }, 1000);
      // });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.preserveData, useFormResult])

  const onSelect = useCallback(
    (name: keyof FieldValues, code?: string | null, label?: string | null, shouldValidate: boolean = true, shouldDirty: boolean = true) => {
      useFormResult.setValue(name as Path<TFieldValues>, code as any, { shouldValidate: shouldValidate, shouldDirty: shouldDirty })

      if (useFormResult.getValues(`${name}_i18nCode` as Path<TFieldValues>) !== undefined) {
        useFormResult.setValue(`${name}_i18nCode` as Path<TFieldValues>, label as any, { shouldDirty: true })
      } else if (useFormResult.getValues(`${name}_value` as Path<TFieldValues>) !== undefined) {
        useFormResult.setValue(`${name}_value` as Path<TFieldValues>, label as any, { shouldDirty: true })
      }
    },
    [useFormResult],
  )

  const handleSubmit: HandleSubmit<TFieldValues> = useCallback(
    (onValid, onSuccess, onInvalid) => {
      const onValidOverridden = (values: TFieldValues) => {
        const onValidRes = onValid(values)
        if (!onValidRes) {
          return
        }
        onValidRes
          .then((displayMessage = true) => {
            if (displayMessage) {
              //   dispatch(
              //     addNotification({
              //       type: 'success',
              //       message: t('SUCCESS'),
              //       duration: 3,
              //     }),
              //   )
            }
          })
          .then(onSuccess)
          .catch((err: ValidationError) => {
            // dispatch(
            //   addNotification({
            //     type: 'error',
            //     message: err.message ?? 'validation error',
            //     duration: 3,
            //   }),
            // )

            if (err.fields) {
              for (const field in err.fields) {
                useFormResult.setError(field as any, {
                  message: err.fields[field],
                })
              }
            }
            // throw err;
          })
      }

      return useFormResult.handleSubmit(onValidOverridden, onInvalid)
    },
    [useFormResult],
  )

  return { ...useFormResult, handleSubmit, onSelect }
}

export default useCustomForm
